import { Link } from "react-router-dom";
import FeedbackForm from "@/pages/about/components/FeedbackForm";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const values = [
  {
    icon: "ri-compass-3-line",
    title: "内容为王",
    desc: "坚持用清晰的分类与详实的信息呈现每一部作品，让观众少走弯路，把时间留给真正想看的剧。",
  },
  {
    icon: "ri-hand-heart-line",
    title: "用户体验优先",
    desc: "从筛选、搜索到收藏，每一步交互都围绕“方便快捷”打磨，让浏览影视资源成为一种享受。",
  },
  {
    icon: "ri-shield-star-line",
    title: "规范透明",
    desc: "所有演示内容均明确标注，不夸大、不误导。我们尊重版权，倡导前往正规授权平台观看。",
  },
  {
    icon: "ri-team-line",
    title: "持续迭代",
    desc: "认真倾听每一位用户的反馈，把每一条建议都转化为下一次更新的动力。",
  },
];

const stats = [
  { value: "24+", label: "演示影视条目" },
  { value: "6", label: "影视分类板块" },
  { value: "3", label: "筛选维度" },
  { value: "100%", label: "前端演示交互" },
];

export default function AboutPage() {
  useDocumentTitle(
    "关于我们 - 91免费影视",
    "了解 91免费影视 的平台定位、发展理念与联系方式。我们专注于提供清晰便捷的影视浏览体验。",
  );

  return (
    <div className="w-full">
      <section className="relative w-full overflow-hidden bg-primary-800">
        <div className="absolute inset-0 opacity-25">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20cinematic%20background%20of%20a%20dark%20blue%20data%20network%20with%20glowing%20film%20reel%20motifs%20and%20soft%20light%20rays%2C%20deep%20navy%20and%20cyan%20tones%2C%20moody%20modern%20lighting%2C%20high%20detail%2C%20no%20text&width=1600&height=600&seq=about-91-01&orientation=landscape"
            alt="91免费影视平台介绍背景"
            className="h-full w-full object-cover"
          />
        </div>
        <div className="relative mx-auto w-full px-4 py-14 md:px-6 md:py-20">
          <nav className="flex items-center gap-1.5 text-sm text-background-300">
            <Link to="/" className="transition-colors hover:text-accent-300">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="font-medium text-background-100">关于我们</span>
          </nav>
          <h1 className="mt-5 max-w-3xl font-heading text-3xl font-bold leading-tight text-background-50 md:text-4xl">
            用更清晰的方式，帮你看懂每一部好作品
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">
            91免费影视平台专注于为影视爱好者提供丰富多样的高清电影与电视剧资源介绍。我们希望通过清晰的分类、便捷的搜索与筛选，成为你浏览影视内容的第一站。
          </p>
        </div>
      </section>

      <div className="mx-auto w-full px-4 py-12 md:px-6 md:py-16">
        <section className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950">
              <i className="ri-book-open-line text-primary-500" />
              网站简介
            </h2>
            <div className="mt-5 space-y-4 text-sm leading-loose text-foreground-700 md:text-base">
              <p>
                91免费影视平台是一个面向广大影视爱好者的内容聚合与浏览平台。我们希望把“找片”这件事变得简单：无论你是想追一部热播剧，还是想按地区、年份、评分筛选一部高分电影，都可以在几秒钟内完成。
              </p>
              <p>
                平台覆盖电影、电视剧、国产剧、海外剧、综艺与动漫六大板块，每个作品都配有封面、年份、地区、类型、评分与剧情简介，帮助你快速判断是否值得一看。
              </p>
              <p className="rounded-lg bg-accent-50 px-4 py-3 text-accent-900">
                <strong>特别说明：</strong>
                本站为演示站点，所有影视名称、人物、评分均为虚构示例，播放与下载功能仅为界面演示，不提供任何真实影视资源。
              </p>
            </div>
          </div>

          <div className="rounded-lg bg-primary-50 p-6 ring-1 ring-primary-100">
            <h3 className="font-heading text-lg font-bold text-foreground-900">平台一览</h3>
            <div className="mt-5 grid grid-cols-2 gap-5">
              {stats.map((item) => (
                <div key={item.label}>
                  <p className="font-heading text-2xl font-bold text-primary-600">{item.value}</p>
                  <p className="mt-1 text-xs text-foreground-600">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mt-14">
          <h2 className="flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950">
            <i className="ri-flag-2-line text-primary-500" />
            团队理念与服务目标
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((item) => (
              <div
                key={item.title}
                className="rounded-lg bg-background-50 p-5 ring-1 ring-background-200 transition duration-300 hover:-translate-y-1 hover:ring-primary-300"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-secondary-100 text-xl text-primary-600">
                  <i className={item.icon} />
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-900">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <h2 className="flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950">
              <i className="ri-contacts-line text-primary-500" />
              联系方式
            </h2>
            <div className="mt-5 space-y-3">
              {[
                { icon: "ri-mail-line", label: "反馈邮箱", value: "feedback@example.com" },
                { icon: "ri-time-line", label: "服务时间", value: "工作日 09:00 - 18:00" },
                { icon: "ri-map-pin-line", label: "办公地点", value: "示例城市 · 演示大厦 8 层" },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-3 rounded-lg bg-background-50 p-4 ring-1 ring-background-200">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-lg text-primary-600">
                    <i className={item.icon} />
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">{item.label}</p>
                    <p className="text-sm font-medium text-foreground-900">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs leading-relaxed text-foreground-400">
              以上联系方式为演示信息。你也可以通过右侧表单随时向我们留言。
            </p>
          </div>

          <div className="lg:col-span-2">
            <h2 className="flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950">
              <i className="ri-chat-smile-2-line text-primary-500" />
              在线反馈
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              无论是功能建议、内容纠错还是合作洽谈，都欢迎告诉我们。
            </p>
            <div className="mt-5">
              <FeedbackForm />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}